/**
 * Посадочная страница после логина.
 *
 * WEB-407 открывал орг-дашборд ВСЕГДА. Отзыв PM 2026-07-25: если активный
 * проект уже выбран, человек ждёт увидеть проект — а видел орг-меню, потому
 * что Sidebar считает режим по адресу страницы (WEB-438), и адрес был орговый.
 * Чиним причину, а не следствие: адрес выбирается по наличию активного
 * проекта, поэтому меню и страница всегда согласованы.
 */
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAppSelector } from '@/hooks/hooks';
import { useOrgRole } from '@/hooks/useOrgRole';
import { Preloader } from '@/components/kit';
import type { OrgRoleLabel, Project } from '@/types';

/**
 * Посадка проекта — его дашборд (WEB-3748 юнит 3, решение владельца 23.10 №7:
 * «/» ведёт на дашборд проекта; ответ reskin 2 — адрес /home, туда же
 * переключатель проекта и «Открыть проект» в приглашении). Было /content.
 */
export const PROJECT_LANDING = '/home';
export const ORG_LANDING = '/org/dashboard';
/** WEB-2466: посадка режима организации для гостя организации. */
export const GUEST_ORG_LANDING = '/projects';

/**
 * WEB-2466 (хвост WEB-761) — посадочная страница РЕЖИМА ОРГАНИЗАЦИИ.
 * Одно правило на все двери входа в режим организации (пункт «Вся
 * организация», переключатель организаций, посадка после перезагрузки):
 * гость организации (роль `member` — позван только в её проекты) идёт в
 * «Проекты», потому что данные «Дашборда» сервер отдаёт только владельцу и
 * администратору; остальные — на «Дашборд». Роль ещё не известна (`null`) —
 * прежнее поведение: страницу дополнительно страхует OrgDashboard.
 */
export function orgModeLanding(role: OrgRoleLabel | null | undefined): string {
  return role === 'member' ? GUEST_ORG_LANDING : ORG_LANDING;
}

export interface LandingInput {
  /** Список проектов хотя бы раз загружен (иначе редиректить рано). */
  loaded: boolean;
  /** Осознанный орг-режим: человек сам выбрал «Вся организация». */
  orgMode: boolean;
  currentProject: Project | null;
  /** WEB-2466: роль в активной организации (как у useOrgRole). */
  orgRole?: OrgRoleLabel | null;
}

/** null = решение ещё не принято, надо подождать загрузку проектов. */
export function resolveLandingRoute({
  loaded,
  orgMode,
  currentProject,
  orgRole = null,
}: LandingInput): string | null {
  if (!loaded) return null;
  if (orgMode) return orgModeLanding(orgRole);
  return currentProject ? PROJECT_LANDING : orgModeLanding(orgRole);
}

export default function LandingRedirect() {
  const { currentProject, orgMode, loaded } = useAppSelector(
    (state) => state.projects,
  );
  // WEB-2466: роль в активной организации — тем же источником, что useOrgRole.
  const { role: orgRole } = useOrgRole();
  const target = resolveLandingRoute({
    loaded,
    orgMode,
    currentProject,
    orgRole,
  });
  if (!target) return <Preloader />;
  return <Navigate to={target} replace />;
}
